import posthog from 'posthog-js';
import { useEffect, type PropsWithChildren } from 'react';
import { PostHogProvider as PHProvider, usePostHog as usePHog } from '@posthog/react';

import type { AuthOrganization, AuthUser } from '../hooks/useAuth';

export function PostHogProvider(props: PropsWithChildren) {
  useEffect(() => {
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
    const posthogToken = import.meta.env.VITE_POSTHOG_API_KEY;
    if (!posthogToken || !posthogHost) return;

    posthog.init(posthogToken, { api_host: posthogHost, defaults: '2026-01-30' });

    // Register properties for all events
    posthog.register({ environment: import.meta.env.MODE });
  }, []);

  return <PHProvider client={posthog}>{props.children}</PHProvider>;
}

export function usePostHog(user?: AuthUser | null, org?: AuthOrganization | null) {
  const ph = usePHog();

  useEffect(() => {
    let userId: string | undefined;
    let properties: Record<string, any> = {};
    if (user) {
      userId = user.id;
      properties.email = user.email;
    }
    if (org) properties.org_id = org.id;

    if (userId) ph?.identify(userId, properties);
  }, [user?.id, user?.email, org?.id, ph]);

  const capture = (event: string, properties?: Record<string, any>) => {
    ph?.capture(event, properties);
  };

  const captureException = (error: Error, properties?: Record<string, any>) => {
    ph?.capture('exception', {
      error_message: error.message,
      error_stack: error.stack,
      ...properties,
    });
  };

  return { capture, captureException };
}
